import {
  assertUnicodeScalarString,
  compareUtf8Bytes
} from "../../game-core/src/utf8";
import { DealProtocolError, fail } from "./errors";

const UTF8_ENCODER = new TextEncoder();
const U32_MAX = 0xffff_ffff;

export { compareUtf8Bytes };

export function assertScalarString(value: string, label: string): void {
  try {
    assertUnicodeScalarString(value, label);
  } catch {
    fail("INVALID_UNICODE_SCALAR", `${label} is not a Unicode scalar string`, label);
  }
}

export function assertCanonicalString(value: string, label: string): void {
  assertScalarString(value, label);
  if (value.length === 0 || value !== value.normalize("NFC")) {
    fail(
      "NON_CANONICAL_STRING",
      `${label} must be non-empty and already NFC normalized`,
      label
    );
  }
}

export function utf8(value: string): Uint8Array {
  assertScalarString(value, "UTF-8 input");
  return UTF8_ENCODER.encode(value);
}

export function concatBytes(
  ...parts: readonly Uint8Array[]
): Uint8Array {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  if (totalLength > U32_MAX) {
    fail("INTEGER_OUT_OF_RANGE", "Concatenated byte string is too long");
  }
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

export function u8(value: number): Uint8Array {
  if (!Number.isInteger(value) || value < 0 || value > 0xff) {
    fail("INTEGER_OUT_OF_RANGE", `Expected U8, received ${String(value)}`);
  }
  return Uint8Array.of(value);
}

export function u32be(value: number): Uint8Array {
  if (!Number.isInteger(value) || value < 0 || value > U32_MAX) {
    fail("INTEGER_OUT_OF_RANGE", `Expected U32, received ${String(value)}`);
  }
  const result = new Uint8Array(4);
  new DataView(result.buffer).setUint32(0, value, false);
  return result;
}

export function lengthPrefixedUtf8(value: string): Uint8Array {
  const encoded = utf8(value);
  return concatBytes(u32be(encoded.length), encoded);
}

export function bytesToHex(bytes: Uint8Array): string {
  let result = "";
  for (const byte of bytes) {
    result += byte.toString(16).padStart(2, "0");
  }
  return result;
}

export function getSubtleCrypto(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) {
    throw new DealProtocolError(
      "CRYPTO_UNAVAILABLE",
      "Web Crypto SubtleCrypto is unavailable"
    );
  }
  return subtle;
}

export async function sha256(
  bytes: Uint8Array,
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<Uint8Array> {
  const stableBuffer = new Uint8Array(bytes);
  const digest = await subtle.digest("SHA-256", stableBuffer);
  return new Uint8Array(digest);
}
